import { useEffect, useState, useMemo, useRef } from 'react'
import { reportsApi, sellersApi, salesApi, settingsApi, customersApi } from '../../services/api'
import {
  LineChart, Line, BarChart, Bar, Cell, ComposedChart, PieChart, Pie,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts'
import { ShoppingCart, DollarSign, UserCheck, Users, MapPin, PackageOpen, IdCard, Phone } from 'lucide-react'
import DateRangePicker from '../../components/DateRangePicker'
import KpiCard from '../../components/KpiCard'
import PanelCard from '../../components/PanelCard'
import StatTileStrip from '../../components/StatTileStrip'

const COLORS = ['#f59e0b', '#10b981', '#3b82f6', '#8b5cf6', '#ef4444', '#06b6d4', '#ec4899', '#14b8a6', '#f97316', '#6366f1']

interface RevenueByDistrict {
  district: string
  total_revenue: number
  sale_count: number
  avg_ticket: number
  orders_total: number
  orders_converted: number
  conversion_rate: number | null
}

interface RevenueByBusinessType {
  business_type_name: string
  total_revenue: number
  sale_count: number
  avg_ticket: number
}

interface CashFlowTrendPoint {
  date: string
  collected: number
  spent: number
  net: number
}

interface CustomersTrendPoint {
  date: string
  new_customers: number
  cumulative_customers: number
}

interface OrdersTrendPoint {
  date: string
  cumulative_agreed: number
  cumulative_cancelled: number
  cumulative_delivered: number
}

interface InventoryAgingItem {
  id: number
  code: string
  start_date: string
  status: string
  kg_remaining: number
  days_open: number
}

interface InventoryStage {
  key: string
  label: string
  dio_applicable: boolean
  dio_avg_days: number | null
  dio_sample_size: number
  turnover_applicable: boolean
  turnover_period_consumed_kg: number | null
  turnover_ending_kg_remaining: number | null
  turnover_ratio_period: number | null
  turnover_ratio_annualized: number | null
  turnover_note: string | null
  aging_count: number
  aging_kg: number
  aging_items: InventoryAgingItem[]
}

export default function AdminDashboardPage() {
  const [report, setReport] = useState<any>(null)
  const [sellers, setSellers] = useState<any[]>([])
  const [sales, setSales] = useState<any[]>([])
  const [customers, setCustomers] = useState<any[]>([])
  const [prefs, setPrefs] = useState<Record<string, boolean>>({})
  const [filterSellerId, setFilterSellerId] = useState('')
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)

  const [cashFlowTrend, setCashFlowTrend] = useState<CashFlowTrendPoint[]>([])
  const [cashFlowLoading, setCashFlowLoading] = useState(true)
  const [cashFlowError, setCashFlowError] = useState('')

  const [customersTrend, setCustomersTrend] = useState<CustomersTrendPoint[]>([])
  const [customersTrendLoading, setCustomersTrendLoading] = useState(true)
  const [customersTrendError, setCustomersTrendError] = useState('')

  const [ordersTrend, setOrdersTrend] = useState<OrdersTrendPoint[]>([])
  const [ordersTrendLoading, setOrdersTrendLoading] = useState(true)
  const [ordersTrendError, setOrdersTrendError] = useState('')

  // Shared date range for the "Facturación por distrito" and "Facturación
  // por tipo de negocio" sections. Both fields optional -- empty means
  // all-time, matching the backend's optional start_date/end_date params.
  const [revenueStartDate, setRevenueStartDate] = useState('')
  const [revenueEndDate, setRevenueEndDate] = useState('')
  const [districtReport, setDistrictReport] = useState<RevenueByDistrict[]>([])
  const [businessTypeReport, setBusinessTypeReport] = useState<RevenueByBusinessType[]>([])
  const [revenueReportsLoading, setRevenueReportsLoading] = useState(true)
  const [districtReportError, setDistrictReportError] = useState('')
  const [businessTypeReportError, setBusinessTypeReportError] = useState('')

  // Own, independent date range for the "Inventario y Rotación" row --
  // deliberately NOT shared with revenueStartDate/revenueEndDate above.
  // This range only affects DIO (days-in-stage) and the turnover numerator
  // (kg consumed in the period); the turnover denominator (current
  // kg_remaining) and the aging-lots snapshot are always "as of today" and
  // ignore this filter entirely.
  const [invRevenueStartDate, setInvRevenueStartDate] = useState('')
  const [invRevenueEndDate, setInvRevenueEndDate] = useState('')
  const [inventoryRotation, setInventoryRotation] = useState<InventoryStage[]>([])
  const [inventoryRotationLoading, setInventoryRotationLoading] = useState(true)
  const [inventoryRotationError, setInventoryRotationError] = useState('')

  const isVisible = (key: string) => prefs[key] !== false
  const sellerIdNum = filterSellerId ? Number(filterSellerId) : undefined
  const latestFilterRef = useRef(filterSellerId)

  useEffect(() => {
    latestFilterRef.current = filterSellerId
    const requestedFilter = filterSellerId

    const fetchData = async () => {
      const [reportResult, sellersResult, salesResult, prefsResult, customersResult] = await Promise.allSettled([
        reportsApi.getSalesReport(sellerIdNum),
        sellersApi.list(),
        salesApi.list(),
        settingsApi.getDashboardPrefs(),
        customersApi.list(),
      ])

      // If the filter changed again while this request was in flight, a
      // newer effect run already owns (or will own) updating state — drop
      // this stale response instead of letting "last response" win over
      // "last selection".
      if (latestFilterRef.current !== requestedFilter) return

      if (reportResult.status === 'fulfilled') {
        setReport(reportResult.value.data)
      }
      if (sellersResult.status === 'fulfilled') {
        setSellers(sellersResult.value.data.sellers || [])
      }
      if (salesResult.status === 'fulfilled') {
        setSales(salesResult.value.data.sales || [])
      }
      if (prefsResult.status === 'fulfilled') {
        setPrefs(prefsResult.value.data.dashboard_prefs || {})
      }
      if (customersResult.status === 'fulfilled') {
        setCustomers(customersResult.value.data.customers || [])
      }

      setLoading(false)
      setRefreshing(false)
    }
    setRefreshing(true)
    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterSellerId])

  useEffect(() => {
    let cancelled = false
    setRevenueReportsLoading(true)
    setDistrictReportError('')
    setBusinessTypeReportError('')

    const fetchRevenueReports = async () => {
      const [districtResult, businessTypeResult] = await Promise.allSettled([
        reportsApi.getByDistrict(revenueStartDate || undefined, revenueEndDate || undefined),
        reportsApi.getByBusinessType(revenueStartDate || undefined, revenueEndDate || undefined),
      ])

      if (cancelled) return

      if (districtResult.status === 'fulfilled') {
        setDistrictReport(districtResult.value.data.by_district || [])
      } else {
        setDistrictReportError('No se pudo cargar la facturación por distrito.')
      }
      if (businessTypeResult.status === 'fulfilled') {
        setBusinessTypeReport(businessTypeResult.value.data.by_business_type || [])
      } else {
        setBusinessTypeReportError('No se pudo cargar la facturación por tipo de negocio.')
      }

      setRevenueReportsLoading(false)
    }
    fetchRevenueReports()
    return () => {
      cancelled = true
    }
  }, [revenueStartDate, revenueEndDate])

  useEffect(() => {
    let cancelled = false
    setInventoryRotationLoading(true)
    setInventoryRotationError('')

    reportsApi.getInventoryRotation(invRevenueStartDate || undefined, invRevenueEndDate || undefined)
      .then((res) => {
        if (cancelled) return
        setInventoryRotation(res.data.stages || [])
      })
      .catch(() => {
        if (!cancelled) setInventoryRotationError('No se pudo cargar la rotación de inventario.')
      })
      .finally(() => {
        if (!cancelled) setInventoryRotationLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [invRevenueStartDate, invRevenueEndDate])

  useEffect(() => {
    let cancelled = false
    setCashFlowLoading(true)
    setCashFlowError('')

    const fetchCashFlowTrend = async () => {
      const [cashFlowResult] = await Promise.allSettled([
        reportsApi.getCashFlowTrend(),
      ])

      if (cancelled) return

      if (cashFlowResult.status === 'fulfilled') {
        setCashFlowTrend(cashFlowResult.value.data.cash_flow_trend || [])
      } else {
        setCashFlowError('No se pudo cargar la tendencia de caja.')
      }

      setCashFlowLoading(false)
    }
    fetchCashFlowTrend()
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    reportsApi.getCustomersTrend()
      .then((res) => {
        if (cancelled) return
        setCustomersTrend(res.data.customers_trend || [])
      })
      .catch(() => {
        if (!cancelled) setCustomersTrendError('No se pudo cargar la tendencia de registro de clientes.')
      })
      .finally(() => {
        if (!cancelled) setCustomersTrendLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    reportsApi.getOrdersTrend()
      .then((res) => {
        if (cancelled) return
        setOrdersTrend(res.data.orders_trend || [])
      })
      .catch(() => {
        if (!cancelled) setOrdersTrendError('No se pudo cargar la tendencia de pedidos.')
      })
      .finally(() => {
        if (!cancelled) setOrdersTrendLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const filteredSales = useMemo(
    () => (filterSellerId ? sales.filter((s) => String(s.seller_id) === filterSellerId) : sales),
    [sales, filterSellerId]
  )

  const salesByDate = useMemo(() => {
    return (report?.by_date || []).map((d: any) => ({
      date: d.date,
      revenue: Number(d.total_revenue || 0),
      sales: d.total_sales,
    })).reverse()
  }, [report])

  const salesByVariant = useMemo(() => {
    const rows = (report?.by_variant || []).map((v: any) => ({
      name: v.variant,
      value: Number(v.total_revenue || 0),
      quantity: v.total_quantity,
    }))
    const total = rows.reduce((sum: number, r: any) => sum + r.value, 0)
    return rows.map((r: any) => ({ ...r, pct: total ? (r.value / total) * 100 : 0 }))
  }, [report])

  const top10Sales = useMemo(() => {
    return [...filteredSales]
      .sort((a, b) => Number(b.total_amount || 0) - Number(a.total_amount || 0))
      .slice(0, 10)
      .map((s) => ({
        id: s.id,
        label: `${s.customer_name || 'Desconocido'} - ${s.variant_name || ''}`,
        customer: s.customer_name || 'Desconocido',
        seller: s.seller_name || '',
        variant: s.variant_name || '',
        total: Number(s.total_amount || 0),
        quantity: s.quantity,
        date: s.sale_date ? new Date(s.sale_date).toLocaleDateString() : '',
      }))
  }, [filteredSales])

  const totalRevenue = useMemo(
    () => filteredSales.reduce((sum, s) => sum + Number(s.total_amount || 0), 0),
    [filteredSales]
  )

  const districtChartData = useMemo(() => {
    const rows = districtReport.map((d) => ({
      district: d.district,
      total_revenue: Number(d.total_revenue || 0),
    }))
    const total = rows.reduce((sum, r) => sum + r.total_revenue, 0)
    return rows.map((r) => ({ ...r, pct: total ? (r.total_revenue / total) * 100 : 0 }))
  }, [districtReport])

  const businessTypeChartData = useMemo(() => {
    const rows = businessTypeReport.map((b) => ({
      business_type_name: b.business_type_name,
      total_revenue: Number(b.total_revenue || 0),
    }))
    const total = rows.reduce((sum, r) => sum + r.total_revenue, 0)
    return rows.map((r) => ({ ...r, pct: total ? (r.total_revenue / total) * 100 : 0 }))
  }, [businessTypeReport])

  const filteredCustomers = useMemo(
    () => (filterSellerId ? customers.filter((c) => String(c.seller_id) === filterSellerId) : customers),
    [customers, filterSellerId]
  )

  const customersByBusinessType = useMemo(() => {
    const counts = new Map<string, number>()
    filteredCustomers.forEach((c) => {
      const key = c.business_type_name || 'Sin tipo'
      counts.set(key, (counts.get(key) || 0) + 1)
    })
    const rows = Array.from(counts.entries())
      .map(([business_type_name, customer_count]) => ({ business_type_name, customer_count }))
      .sort((a, b) => b.customer_count - a.customer_count)
    const total = rows.reduce((sum, r) => sum + r.customer_count, 0)
    return rows.map((r) => ({ ...r, pct: total ? (r.customer_count / total) * 100 : 0 }))
  }, [filteredCustomers])

  const dioChartData = useMemo(() => {
    return inventoryRotation.map((s) => ({
      key: s.key,
      label: s.label,
      dio_avg_days: s.dio_applicable ? Number(s.dio_avg_days || 0) : 0,
      dio_applicable: s.dio_applicable,
      dio_sample_size: s.dio_sample_size,
    }))
  }, [inventoryRotation])

  const turnoverChartData = useMemo(() => {
    return inventoryRotation.map((s) => ({
      key: s.key,
      label: s.label,
      turnover_ratio_annualized: s.turnover_applicable ? Number(s.turnover_ratio_annualized || 0) : 0,
      turnover_applicable: s.turnover_applicable,
      turnover_note: s.turnover_note,
      turnover_period_consumed_kg: Number(s.turnover_period_consumed_kg || 0),
      turnover_ending_kg_remaining: Number(s.turnover_ending_kg_remaining || 0),
    }))
  }, [inventoryRotation])

  const agingChartData = useMemo(() => {
    return inventoryRotation.map((s) => ({
      key: s.key,
      label: s.label,
      aging_count: s.aging_count || 0,
      aging_kg: Number(s.aging_kg || 0),
    }))
  }, [inventoryRotation])

  const agingTotals = useMemo(() => {
    return inventoryRotation.reduce(
      (acc, s) => ({
        count: acc.count + (s.aging_count || 0),
        kg: acc.kg + Number(s.aging_kg || 0),
      }),
      { count: 0, kg: 0 }
    )
  }, [inventoryRotation])

  const geoCoverage = useMemo(() => ({
    customers: {
      covered: filteredCustomers.filter((c) => c.latitude != null && c.longitude != null).length,
      total: filteredCustomers.length,
    },
    sales: {
      covered: filteredSales.filter((s) => s.gps_latitude != null && s.gps_longitude != null).length,
      total: filteredSales.length,
    },
  }), [filteredCustomers, filteredSales])

  const dataQualityCoverage = useMemo(() => ({
    dniOrRuc: {
      covered: filteredCustomers.filter((c) => (c.dni && c.dni.trim() !== '') || (c.ruc && c.ruc.trim() !== '')).length,
      total: filteredCustomers.length,
    },
    phone: {
      covered: filteredCustomers.filter((c) => c.phone && c.phone.trim() !== '').length,
      total: filteredCustomers.length,
    },
  }), [filteredCustomers])

  if (loading) {
    return <div className="text-center py-8">Cargando...</div>
  }

  const filteredSellerName = sellers.find((s) => String(s.id) === filterSellerId)?.full_name

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-espresso-800">
          Panel de Administrador
          {filteredSellerName && (
            <span className="text-lg font-normal text-espresso-500"> — {filteredSellerName}</span>
          )}
        </h1>
        <div className="flex items-center gap-2">
          <label htmlFor="seller-filter" className="text-sm text-espresso-600">
            Vendedor:
          </label>
          <select
            id="seller-filter"
            value={filterSellerId}
            onChange={(e) => setFilterSellerId(e.target.value)}
            className="border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
          >
            <option value="">Todos los vendedores</option>
            {sellers.map((s) => (
              <option key={s.id} value={s.id}>
                {s.full_name}{s.status !== 'active' ? ' (inactivo)' : ''}
              </option>
            ))}
          </select>
          {refreshing && <span className="text-xs text-espresso-500">Actualizando...</span>}
        </div>
      </div>

      {/* Summary Cards */}
      <h2 className="mb-3 text-xs font-semibold uppercase tracking-wider text-espresso-500">
        Resumen
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <KpiCard
          icon={ShoppingCart}
          label="Ventas Totales"
          value={report?.summary?.total_sales || 0}
          accent="terracotta"
        />
        <KpiCard
          icon={DollarSign}
          label="Ingresos Totales"
          value={`$${totalRevenue.toFixed(2)}`}
          accent="green"
        />
        <KpiCard
          icon={UserCheck}
          label="Vendedores Activos"
          value={sellers.filter(s => s.status === 'active').length}
          accent="blue"
        />
        <KpiCard
          icon={Users}
          label="Total de Vendedores"
          value={sellers.length}
          accent="purple"
        />
      </div>

      <div className="mb-8">
        <StatTileStrip
          tiles={[
            {
              icon: MapPin,
              accent: 'terracotta',
              value: geoCoverage.customers.total ? `${((geoCoverage.customers.covered / geoCoverage.customers.total) * 100).toFixed(0)}%` : '—',
              label: 'Cobertura de Ubicación — Clientes',
              subtext: `${geoCoverage.customers.covered} de ${geoCoverage.customers.total} clientes activos`,
            },
            {
              icon: MapPin,
              accent: 'blue',
              value: geoCoverage.sales.total ? `${((geoCoverage.sales.covered / geoCoverage.sales.total) * 100).toFixed(0)}%` : '—',
              label: 'Cobertura de Ubicación — Ventas',
              subtext: `${geoCoverage.sales.covered} de ${geoCoverage.sales.total} ventas`,
            },
            {
              icon: IdCard,
              accent: 'green',
              value: dataQualityCoverage.dniOrRuc.total ? `${((dataQualityCoverage.dniOrRuc.covered / dataQualityCoverage.dniOrRuc.total) * 100).toFixed(0)}%` : '—',
              label: 'Clientes con DNI/RUC',
              subtext: `${dataQualityCoverage.dniOrRuc.covered} de ${dataQualityCoverage.dniOrRuc.total} clientes activos`,
            },
            {
              icon: Phone,
              accent: 'purple',
              value: dataQualityCoverage.phone.total ? `${((dataQualityCoverage.phone.covered / dataQualityCoverage.phone.total) * 100).toFixed(0)}%` : '—',
              label: 'Clientes con Teléfono',
              subtext: `${dataQualityCoverage.phone.covered} de ${dataQualityCoverage.phone.total} clientes activos`,
            },
          ]}
        />
      </div>

      {/* Fila 1: Tendencias (series de tiempo) */}
      <h2 className="mb-3 text-xs font-semibold uppercase tracking-wider text-espresso-500">
        Tendencias
      </h2>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {isVisible('admin.sales_trend') && (
          <PanelCard title="Tendencia de Ventas (Últimos 30 Días)">
          {salesByDate.length === 0 ? (
            <p className="text-espresso-500">No hay datos de ventas disponibles.</p>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={salesByDate}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip formatter={(value: number) => [`$${value.toFixed(2)}`, 'Ingresos']} />
                <Legend />
                <Line type="monotone" dataKey="revenue" stroke="#f59e0b" strokeWidth={2} name="Ingresos ($)" />
              </LineChart>
            </ResponsiveContainer>
          )}
          </PanelCard>
        )}

        {isVisible('admin.cash_flow_trend') && (
          <PanelCard
            title="Tendencia de Caja"
            subtitle="Cobrado, gastado y neto por día, para toda la empresa. No respeta el filtro de vendedor de este panel, porque los gastos no tienen vendedor asociado."
          >
          {cashFlowLoading ? (
            <p className="text-espresso-500">Cargando...</p>
          ) : cashFlowError ? (
            <p className="text-red-600 text-sm">{cashFlowError}</p>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={cashFlowTrend}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip formatter={(value: number) => [`$${Number(value).toFixed(2)}`, '']} />
                <Legend />
                <Line type="monotone" dataKey="collected" stroke="#10b981" strokeWidth={2} name="Cobrado ($)" />
                <Line type="monotone" dataKey="spent" stroke="#ef4444" strokeWidth={2} name="Gastado ($)" />
                <Line type="monotone" dataKey="net" stroke="#3b82f6" strokeWidth={2} strokeDasharray="4 2" name="Neto ($)" />
              </LineChart>
            </ResponsiveContainer>
          )}
          </PanelCard>
        )}

        {isVisible('admin.customers_trend') && (
          <PanelCard title="Tendencia de Registro de Clientes">
          {customersTrendLoading ? (
            <p className="text-espresso-500">Cargando...</p>
          ) : customersTrendError ? (
            <p className="text-red-600 text-sm">{customersTrendError}</p>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <ComposedChart data={customersTrend}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                <YAxis yAxisId="left" tick={{ fontSize: 12 }} allowDecimals={false} />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12 }} allowDecimals={false} />
                <Tooltip />
                <Legend />
                <Bar yAxisId="left" dataKey="new_customers" name="Nuevos" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                <Line yAxisId="right" type="monotone" dataKey="cumulative_customers" name="Acumulado" stroke="#3b82f6" strokeWidth={2} />
              </ComposedChart>
            </ResponsiveContainer>
          )}
          </PanelCard>
        )}

        {isVisible('admin.orders_trend') && (
          <PanelCard title="Tendencia de Pedidos">
          {ordersTrendLoading ? (
            <p className="text-espresso-500">Cargando...</p>
          ) : ordersTrendError ? (
            <p className="text-red-600 text-sm">{ordersTrendError}</p>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={ordersTrend}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="cumulative_agreed" stroke="#f59e0b" strokeWidth={2} name="Pactados" />
                <Line type="monotone" dataKey="cumulative_cancelled" stroke="#ef4444" strokeWidth={2} name="Cancelados" />
                <Line type="monotone" dataKey="cumulative_delivered" stroke="#10b981" strokeWidth={2} name="Entregados" />
              </LineChart>
            </ResponsiveContainer>
          )}
          </PanelCard>
        )}
      </div>

      {/* Fila 2: Distribuciones porcentuales y ranking — scroll horizontal */}
      <h2 className="mb-3 text-xs font-semibold uppercase tracking-wider text-espresso-500">
        Distribuciones y Ranking
      </h2>
      {(isVisible('admin.revenue_by_district') || isVisible('admin.revenue_by_business_type')) && (
        <div className="bg-cream-50 shadow rounded-lg border border-cream-200 p-4 mb-4">
          <DateRangePicker
            idPrefix="revenue-range"
            label="Rango de fechas (Facturación por distrito / tipo de negocio):"
            startDate={revenueStartDate}
            endDate={revenueEndDate}
            onStartDateChange={setRevenueStartDate}
            onEndDateChange={setRevenueEndDate}
          />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {isVisible('admin.revenue_by_product') && (
          <PanelCard title="Ingresos por Producto" subtitle="% del total de ingresos que aporta cada producto.">
          {salesByVariant.length === 0 ? (
            <p className="text-espresso-500">No hay datos de ventas disponibles.</p>
          ) : (
            <div className="flex items-center gap-4">
              <ul className="flex-1 min-w-0 space-y-2 max-h-[260px] overflow-y-auto">
                {salesByVariant.map((row: any, index: number) => (
                  <li key={row.name} className="flex items-center gap-2 text-xs text-espresso-700">
                    <span className="h-3 w-3 rounded-sm shrink-0" style={{ backgroundColor: COLORS[index % COLORS.length] }} />
                    <span className="truncate">{row.name}</span>
                    <span className="ml-auto shrink-0 font-medium text-espresso-500">{row.pct.toFixed(1)}%</span>
                  </li>
                ))}
              </ul>
              <ResponsiveContainer width={160} height={180}>
                <PieChart>
                  <Pie data={salesByVariant} dataKey="pct" nameKey="name" innerRadius={45} outerRadius={70} paddingAngle={2}>
                    {salesByVariant.map((_: any, index: number) => (
                      <Cell key={index} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value: number, _name: string, props: any) => [`${value.toFixed(1)}% ($${props.payload.value.toFixed(2)})`, props.payload.name]} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
          </PanelCard>
        )}

        {isVisible('admin.revenue_by_district') && (
          <PanelCard
            title="Facturación por distrito"
            subtitle={'% del total facturado por distrito del cliente (no descuenta costos, no es rentabilidad). Incluye clientes sin distrito registrado bajo "Sin distrito".'}
          >
          {revenueReportsLoading ? (
            <p className="text-espresso-500">Cargando...</p>
          ) : districtReportError ? (
            <p className="text-red-600 text-sm">{districtReportError}</p>
          ) : districtChartData.length === 0 ? (
            <p className="text-espresso-500">No hay datos para el rango seleccionado.</p>
          ) : (
            <div className="overflow-y-auto" style={{ height: 300 }}>
              <ResponsiveContainer width="100%" height={Math.max(220, districtChartData.length * 40)}>
                <BarChart data={districtChartData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" tick={{ fontSize: 11 }} unit="%" domain={[0, 100]} />
                  <YAxis type="category" dataKey="district" tick={{ fontSize: 11 }} width={120} />
                  <Tooltip formatter={(value: number, _name: string, props: any) => [`${value.toFixed(1)}% ($${props.payload.total_revenue.toFixed(2)})`, 'Facturación']} />
                  <Bar dataKey="pct" name="% de Facturación" radius={[0, 4, 4, 0]}>
                    {districtChartData.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
          </PanelCard>
        )}

        {isVisible('admin.revenue_by_business_type') && (
          <PanelCard
            title="Facturación por tipo de negocio"
            subtitle={'% del total facturado por tipo de negocio del cliente (no descuenta costos, no es rentabilidad). Incluye clientes sin tipo registrado bajo "Sin tipo".'}
          >
          {revenueReportsLoading ? (
            <p className="text-espresso-500">Cargando...</p>
          ) : businessTypeReportError ? (
            <p className="text-red-600 text-sm">{businessTypeReportError}</p>
          ) : businessTypeChartData.length === 0 ? (
            <p className="text-espresso-500">No hay datos para el rango seleccionado.</p>
          ) : (
            <div className="overflow-y-auto" style={{ height: 300 }}>
              <ResponsiveContainer width="100%" height={Math.max(220, businessTypeChartData.length * 40)}>
                <BarChart data={businessTypeChartData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" tick={{ fontSize: 11 }} unit="%" domain={[0, 100]} />
                  <YAxis type="category" dataKey="business_type_name" tick={{ fontSize: 11 }} width={120} />
                  <Tooltip formatter={(value: number, _name: string, props: any) => [`${value.toFixed(1)}% ($${props.payload.total_revenue.toFixed(2)})`, 'Facturación']} />
                  <Bar dataKey="pct" name="% de Facturación" radius={[0, 4, 4, 0]}>
                    {businessTypeChartData.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
          </PanelCard>
        )}

        {isVisible('admin.customers_by_business_type') && (
          <PanelCard
            title="Clientes por tipo de negocio"
            subtitle="% de clientes activos por tipo de negocio (no es facturación). Foto actual del padrón de clientes, no está acotada por fecha."
          >
          {customersByBusinessType.length === 0 ? (
            <p className="text-espresso-500">No hay clientes registrados.</p>
          ) : (
            <div className="overflow-y-auto" style={{ height: 300 }}>
              <ResponsiveContainer width="100%" height={Math.max(220, customersByBusinessType.length * 40)}>
                <BarChart data={customersByBusinessType} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" tick={{ fontSize: 11 }} unit="%" domain={[0, 100]} />
                  <YAxis type="category" dataKey="business_type_name" tick={{ fontSize: 11 }} width={120} />
                  <Tooltip formatter={(value: number, _name: string, props: any) => [`${value.toFixed(1)}% (${props.payload.customer_count})`, 'Clientes']} />
                  <Bar dataKey="pct" name="% de Clientes" radius={[0, 4, 4, 0]}>
                    {customersByBusinessType.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
          </PanelCard>
        )}

        {isVisible('admin.top_sales') && (
          <PanelCard title="Top 10 Ventas por Total">
          {top10Sales.length === 0 ? (
            <p className="text-espresso-500">No hay datos de ventas disponibles.</p>
          ) : (
            <div className="overflow-y-auto max-h-[300px]">
              <table className="w-full text-xs">
                <tbody>
                  {top10Sales.map((s, index) => {
                    const max = top10Sales[0]?.total || 1
                    return (
                      <tr key={s.id} className="border-b border-cream-100 last:border-0">
                        <td className="py-2 pr-2 align-top">
                          <div className="font-medium text-espresso-800 truncate max-w-[160px]">{s.customer}</div>
                          <div className="text-espresso-400 truncate max-w-[160px]">{s.variant} · {s.date}</div>
                        </td>
                        <td className="py-2 align-top">
                          <div className="flex items-center justify-end gap-2">
                            <div className="h-1.5 w-14 shrink-0 rounded-full bg-cream-200 overflow-hidden">
                              <div
                                className="h-full rounded-full"
                                style={{ width: `${(s.total / max) * 100}%`, backgroundColor: COLORS[index % COLORS.length] }}
                              />
                            </div>
                            <span className="font-semibold text-espresso-800 whitespace-nowrap">${s.total.toFixed(2)}</span>
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
          </PanelCard>
        )}
      </div>

      {/* Fila 3: Inventario y Rotación — foto por etapa de la cadena de producción */}
      {(isVisible('admin.inventory_dio') || isVisible('admin.inventory_turnover') || isVisible('admin.inventory_aging')) && (
      <>
        <h2 className="mb-3 text-xs font-semibold uppercase tracking-wider text-espresso-500">
          Inventario y Rotación
        </h2>
        <p className="text-xs text-espresso-500 mb-3">
          El rango de fechas afecta los días promedio en etapa y el consumo del período; el
          inventario actual y los lotes en envejecimiento siempre reflejan el estado de hoy.
        </p>
        <div className="bg-cream-50 shadow rounded-lg border border-cream-200 p-4 mb-4">
          <DateRangePicker
            idPrefix="inventory-rotation-range"
            label="Rango de fechas (DIO y consumo del período):"
            startDate={invRevenueStartDate}
            endDate={invRevenueEndDate}
            onStartDateChange={setInvRevenueStartDate}
            onEndDateChange={setInvRevenueEndDate}
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {isVisible('admin.inventory_dio') && (
            <PanelCard
              title="Días promedio en etapa (DIO)"
              subtitle="Días promedio que un lote permanece abierto en cada etapa antes de completarse."
            >
            {inventoryRotationLoading ? (
              <p className="text-espresso-500">Cargando...</p>
            ) : inventoryRotationError ? (
              <p className="text-red-600 text-sm">{inventoryRotationError}</p>
            ) : dioChartData.length === 0 ? (
              <p className="text-espresso-500">No hay datos para el rango seleccionado.</p>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={dioChartData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" tick={{ fontSize: 11 }} unit=" días" />
                  <YAxis type="category" dataKey="label" tick={{ fontSize: 11 }} width={140} />
                  <Tooltip
                    formatter={(value: number, _name: string, props: any) => {
                      const row = props.payload
                      if (!row.dio_applicable) {
                        return ['No aplica — selección es un paso único, sin fecha de cierre', 'DIO']
                      }
                      return [`${value.toFixed(1)} días (n=${row.dio_sample_size})`, 'DIO']
                    }}
                  />
                  <Bar dataKey="dio_avg_days" name="Días promedio en etapa" radius={[0, 4, 4, 0]}>
                    {dioChartData.map((row, i) => (
                      <Cell key={row.key} fill={row.dio_applicable ? COLORS[i % COLORS.length] : '#d1d5db'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
            </PanelCard>
          )}

          {isVisible('admin.inventory_turnover') && (
            <PanelCard
              title="Rotación aproximada (anualizada)"
              subtitle="Aproximación: kg consumidos en el período ÷ stock actual (no el de fin de período)."
            >
            {inventoryRotationLoading ? (
              <p className="text-espresso-500">Cargando...</p>
            ) : inventoryRotationError ? (
              <p className="text-red-600 text-sm">{inventoryRotationError}</p>
            ) : turnoverChartData.length === 0 ? (
              <p className="text-espresso-500">No hay datos para el rango seleccionado.</p>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={turnoverChartData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" tick={{ fontSize: 11 }} unit="x" />
                  <YAxis type="category" dataKey="label" tick={{ fontSize: 11 }} width={140} />
                  <Tooltip
                    formatter={(value: number, _name: string, props: any) => {
                      const row = props.payload
                      if (!row.turnover_applicable) {
                        return [row.turnover_note || 'No aplica', 'Rotación anualizada']
                      }
                      return [
                        `${value.toFixed(2)}x/año (${row.turnover_period_consumed_kg.toFixed(1)} kg consumidos ÷ ${row.turnover_ending_kg_remaining.toFixed(1)} kg en stock)`,
                        'Rotación anualizada',
                      ]
                    }}
                  />
                  <Bar dataKey="turnover_ratio_annualized" name="Rotación anualizada" radius={[0, 4, 4, 0]}>
                    {turnoverChartData.map((row, i) => (
                      <Cell key={row.key} fill={row.turnover_applicable ? COLORS[i % COLORS.length] : '#d1d5db'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
            </PanelCard>
          )}

          {isVisible('admin.inventory_aging') && (
            <PanelCard
              title="Lotes en envejecimiento"
              subtitle="Lotes abiertos por más tiempo del umbral configurado en el backend, a la fecha de hoy."
            >
            {inventoryRotationLoading ? (
              <p className="text-espresso-500">Cargando...</p>
            ) : inventoryRotationError ? (
              <p className="text-red-600 text-sm">{inventoryRotationError}</p>
            ) : agingChartData.length === 0 ? (
              <p className="text-espresso-500">No hay datos disponibles.</p>
            ) : (
              <>
                <KpiCard
                  icon={PackageOpen}
                  label="Lotes en envejecimiento (todas las etapas)"
                  value={agingTotals.count}
                  subtext={`${agingTotals.kg.toFixed(1)} kg en total`}
                  accent="amber"
                />
                <div className="mt-4">
                  <ResponsiveContainer width="100%" height={180}>
                    <BarChart data={agingChartData} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                      <YAxis type="category" dataKey="label" tick={{ fontSize: 11 }} width={140} />
                      <Tooltip
                        formatter={(value: number, _name: string, props: any) => [
                          `${value} lotes (${props.payload.aging_kg.toFixed(1)} kg)`,
                          'En envejecimiento',
                        ]}
                      />
                      <Bar dataKey="aging_count" name="Lotes en envejecimiento" radius={[0, 4, 4, 0]}>
                        {agingChartData.map((row, i) => (
                          <Cell key={row.key} fill={COLORS[i % COLORS.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </>
            )}
            </PanelCard>
          )}
        </div>
      </>
      )}
    </div>
  )
}
